import { animated, config, easings, useSpring } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled, { keyframes } from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import { BrandL } from '@sorare/core/src/atoms/typography';
import { CardImg } from '@sorare/core/src/components/card/CardImg';
import { OnboardingButton } from '@sorare/core/src/components/onboarding/OnboardingButton';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { skeletonStyle } from '@sorare/core/src/style/utils';

import {
  BaseLayout,
  BottomArea,
  CardWrapper,
  CenterArea,
  TopArea,
} from '#src/components/commonCard/RevealPackCardsDialog/ui.ts';

import packBack from './assets/packBack.webp';

const clipPath = `
    0% 10.2%,
    6% 9.5%,
    20% 10.5%,
    25.5% 9.8%,
    29% 10.8%,
    33.8% 9.3%,
    43.5% 10.5%,
    59.5% 9.7%,
    69% 11.5%,
    76.5% 10.1%,
    80% 10.3%,
    83.3% 10.1%,
    85.8% 9.6%,
    91.2% 11.2%,
    98.5% 10.6%,
    100% 10.9%,
`;

const AnimatedDiv = styled(animated.div)`
  flex: 1;
  display: flex;
`;

const fadeInUp = keyframes`
  0% {
    transform: translateY(calc(60 * var(--vh)));
  }
  33% {
    opacity: 0;
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const CenterWrapper = styled.div`
  position: relative;
  aspect-ratio: var(--pack-aspect-ratio);
  width: 100%;
  @media ${laptopAndAbove} {
    transform: translateY(calc(60 * var(--vh)));
    opacity: 0;

    animation: ${fadeInUp} cubic-bezier(0.03, 0.47, 0.56, 0.96) forwards;
    animation-delay: 0.5s;
    animation-duration: 0.7s;
  }
`;
const PackWrapper = styled(animated.div)`
  position: absolute;
  top: 0;
  width: 100%;
  aspect-ratio: var(--pack-aspect-ratio);
  z-index: 1;
`;
const PackBackWrapper = styled(PackWrapper)`
  z-index: -1;
`;
const PackSkeleton = styled(PackWrapper)`
  position: relative;
  ${skeletonStyle}
`;
const CardMovementWrapper = styled(animated.div)`
  isolation: isolate;
  width: var(--card-width);
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(
    -50%,
    calc(var(--translate-factor) * var(--stripe-height))
  );
`;
const PackBottomImg = styled(CardImg)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  transition: opacity ease-in 1s 0.5s;
  clip-path: polygon(${clipPath} 100% 100%, 0% 100%);
  aspect-ratio: var(--pack-aspect-ratio);
`;
const PackBackImg = styled.img.attrs({ src: packBack })`
  position: absolute;
  width: 100%;
  top: calc(var(--stripe-height) - 6px);
`;
const TopStripeClip = styled.div`
  position: absolute;
  width: 100%;
  transform: translate(0, 1px);
  clip-path: polygon(
    var(--progressPercentage) 0%,
    100% 0%,
    100% 100%,
    calc(var(--progressPercentage) - ((1 - var(--progress) / 100) * 20px))
      var(--stripe-height)
  );
`;
const TopStripeImg = styled(CardImg)`
  width: 100%;
  clip-path: polygon(0% 0%, ${clipPath} 100% 0%);
`;
const DanglingStripeWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  transform-origin: top left;
  overflow: hidden;
  clip-path: polygon(
    0% 0%,
    100% 0%,
    100% var(--stripe-height),
    50px var(--stripe-height)
  );
  transform: translate(var(--progressPercentage))
    rotate(calc((1 - var(--progress) / 300) * 90deg));
`;
const DanglingStripeImg = styled(TopStripeImg)`
  position: absolute;
  transform: rotateY(-180deg) translate(calc(100% - var(--progressPercentage)));
`;
const DragDiv = styled.div`
  position: absolute;
  inset: 0 -200px;
  touch-action: none;
`;
const DashedLineWrapper = styled(Vertical)`
  position: absolute;
  top: var(--stripe-height);
  width: 100%;
  overflow: hidden;
`;
const DashedLineSVG = styled(animated.svg)`
  width: 100%;
  height: 3px;
  line {
    stroke: var(--c-white);
    stroke-width: 3px;
    stroke-dasharray: 5% 2%;
  }
`;
const BrightEffectWrapper = styled.div`
  position: absolute;
  top: 0;
  opacity: 1;
  z-index: 1;

  &.opened {
    opacity: 0;
    transition: opacity linear 0.5s 0.45s;
  }
`;
const BrightEffect = styled.div`
  filter: brightness(15) blur(2px) drop-shadow(0 0 30px #ffffff);
  opacity: calc((var(--progress) - 30) / 100);

  &.opened {
    opacity: 1;
    transition: opacity linear 0.4s;
  }
`;
const ClipPath = styled.div`
  clip-path: rect(0 100% calc(100% - var(--stripe-height) - 2px) 0);
`;
const CardOpacityWrapper = styled(CardWrapper)`
  opacity: 0;

  &.opened {
    opacity: 1;
    transition: opacity linear 0.1s 0.2s;
  }
`;
const OpenButtonWrapper = styled(Horizontal).attrs({ center: true })`
  height: 100%;
`;
const CloseButtonWrapper = styled.div`
  grid-area: topRight;
`;
const StyledBrandL = styled(BrandL)`
  grid-area: topCenter;
  align-self: center;
  text-align: center;
`;

type Props = {
  firstCardUrl: string;
  onDone: () => void;
  packPictureUrl?: string;
  loading: boolean;
  closeButton: ReactNode;
  title?: ReactNode;
};
export const PackTear = ({
  firstCardUrl,
  onDone,
  packPictureUrl,
  loading,
  closeButton,
  title,
}: Props) => {
  const [pixelsTeared, setPixelsTeared] = useState(0);
  const [packOpened, setPackOpened] = useState(false);
  const [verticalMovementStarted, setVerticalMovementStarted] = useState(false);
  const packRef = useRef<HTMLDivElement>(null);

  const [tearAnimationSpring, tearAnimationSpringApi] = useSpring(() => ({
    from: {
      '--progress': 0,
      '--progressPercentage': '0%',
    },
  }));
  const animateProgress = (progress: number) => {
    tearAnimationSpringApi.start({
      '--progress': progress,
      '--progressPercentage': `${progress}%`,
    });
  };

  const [dashedLineStyle] = useSpring(() => ({
    from: {
      transform: `translate(-100%)`,
    },
    to: {
      transform: `translate(100%)`,
    },
    loop: true,
    config: config.slow,
  }));
  const [packDownSpring, packDownApi] = useSpring(() => ({
    from: {
      transform: 'translateY(0px) rotate(0deg)',
    },
  }));
  const [verticalMovementStyle, verticalMovementApi] = useSpring(() => ({
    from: {
      '--translate-factor': 1,
    },
  }));

  if (packOpened && !verticalMovementStarted) {
    verticalMovementApi.start({
      '--translate-factor': -1,
      config: { duration: 400, easing: easings.easeInOutQuad },
      onRest: () => {
        verticalMovementApi.start({
          '--translate-factor': 0,
          config: { duration: 500, easing: easings.easeInOutQuad },
          onRest: onDone,
        });
      },
    });
    setVerticalMovementStarted(true);
  }

  const movePackDown = () => {
    packDownApi.start({
      transform: 'translateY(700px) rotate(5deg)',
      config: { duration: 600, easing: easings.easeInQuad },
    });
  };
  const fullyTear = () => {
    animateProgress(120);
    setPackOpened(true);
    movePackDown();
  };

  const bind = useDrag(
    ({ xy: [x], delta: [xDelta], initial: [gestureStartPosition], last }) => {
      if (packRef.current) {
        const { left: packLeftCoordinate, width } =
          packRef.current?.getBoundingClientRect() || {};
        const gestureStartsInAuthorizedZone =
          pixelsTeared === 0
            ? gestureStartPosition < packLeftCoordinate + width / 2
            : true;

        if (gestureStartsInAuthorizedZone) {
          const progress = (100 * (x - packLeftCoordinate)) / width;
          if (progress < 120) {
            animateProgress(progress);
          }
          if (progress >= 95 && !packOpened) {
            setPackOpened(true);
            movePackDown();
          }
        }

        if ((last || xDelta < 0) && x > pixelsTeared) {
          setPixelsTeared(x);
        }
      }
    }
  );

  return (
    <AnimatedDiv style={tearAnimationSpring as CSSProperties}>
      <BaseLayout>
        <TopArea>
          {title ? <StyledBrandL>{title}</StyledBrandL> : null}
          <CloseButtonWrapper>{closeButton}</CloseButtonWrapper>
        </TopArea>

        <CenterArea>
          {loading ? (
            <PackSkeleton />
          ) : (
            <CenterWrapper>
              <PackBackWrapper style={packDownSpring}>
                <PackBackImg />
              </PackBackWrapper>
              <PackWrapper ref={packRef} style={packDownSpring}>
                <PackBottomImg src={packPictureUrl} width={320} />
                <TopStripeClip>
                  <TopStripeImg src={packPictureUrl} width={320} />
                </TopStripeClip>
                <DanglingStripeWrapper>
                  <DanglingStripeImg src={packPictureUrl} width={320} />
                </DanglingStripeWrapper>
                {pixelsTeared === 0 && (
                  <DashedLineWrapper>
                    <DashedLineSVG style={dashedLineStyle}>
                      <line x1="0" y1="1.5" x2="40%" y2="1.5" />
                    </DashedLineSVG>
                  </DashedLineWrapper>
                )}
                <DragDiv {...bind()} />
              </PackWrapper>
              <CardMovementWrapper
                style={verticalMovementStyle as CSSProperties}
              >
                <CardOpacityWrapper
                  className={classNames({ opened: packOpened })}
                >
                  <CardImg width={320} src={firstCardUrl} draggable={false} />
                </CardOpacityWrapper>
                <BrightEffectWrapper
                  className={classNames({ opened: packOpened })}
                >
                  <BrightEffect className={classNames({ opened: packOpened })}>
                    <ClipPath>
                      <CardImg
                        width={320}
                        src={firstCardUrl}
                        draggable={false}
                      />
                    </ClipPath>
                  </BrightEffect>
                </BrightEffectWrapper>
              </CardMovementWrapper>
            </CenterWrapper>
          )}
        </CenterArea>
        <BottomArea className={classNames({ hidden: packOpened })}>
          <OpenButtonWrapper>
            <Skeleton loading={loading}>
              <OnboardingButton
                size="medium"
                color="quaternary"
                onClick={fullyTear}
              >
                <FormattedMessage defaultMessage="Open" />
              </OnboardingButton>
            </Skeleton>
          </OpenButtonWrapper>
        </BottomArea>
      </BaseLayout>
    </AnimatedDiv>
  );
};
